import { Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { Separator } from '@/components/ui/separator';
import { Mail, MapPin, Globe } from 'lucide-react';
import i18n from 'i18next';

const languages = [
  { code: 'en', label: 'EN' },
  { code: 'de', label: 'DE' },
  { code: 'es', label: 'ES' },
];

export default function Footer() {
  const { t } = useTranslation();
  const currentLang = i18n.language || 'en';

  const changeLanguage = (code) => {
    i18n.changeLanguage(code);
    localStorage.setItem('energiesse-lang', code);
  };

  const navLinks = [
    { path: '/', label: t('nav.home') },
    { path: '/about', label: t('nav.about') },
    { path: '/services', label: t('nav.services') },
    { path: '/projects', label: t('nav.projects') },
    { path: '/trust', label: t('nav.trust') },
    { path: '/partnerships', label: t('nav.partnerships') },
    { path: '/contact', label: t('nav.contact') },
  ];

  return (
    <footer
      className="bg-[hsl(222,22%,10%)] text-[hsl(210,40%,88%)]"
      data-testid="site-footer"
    >
      <div className="container-site py-12 md:py-16">
        <div className="grid grid-cols-1 md:grid-cols-3 gap-10 md:gap-8">
          {/* Column 1: Company */}
          <div className="space-y-4">
            <div className="flex items-center gap-2">
              <div className="w-7 h-7 rounded-md bg-[var(--energy-green)] flex items-center justify-center flex-shrink-0">
                <svg width="16" height="16" viewBox="0 0 16 16" fill="none">
                  <path d="M9 2L4 9h5l-2 5 7-8H9l2-4H9z" fill="white" />
                </svg>
              </div>
              <span className="font-heading font-semibold text-base text-white">
                {t('footer.company')}
              </span>
            </div>
            <p className="text-sm text-[hsl(215,12%,62%)] leading-relaxed">
              {t('footer.tagline')}
            </p>
            <div className="flex items-center gap-2 text-sm text-[hsl(215,12%,62%)]">
              <MapPin className="h-3.5 w-3.5 text-[var(--energy-green)] flex-shrink-0" />
              <span>{t('footer.address')}</span>
            </div>
            <div className="flex items-center gap-2 text-sm">
              <Mail className="h-3.5 w-3.5 text-[var(--energy-green)] flex-shrink-0" />
              <a
                href={`mailto:${t('footer.email')}`}
                className="text-[hsl(215,12%,72%)] hover:text-white transition-colors"
                data-testid="footer-email"
              >
                {t('footer.email')}
              </a>
            </div>
            <div className="flex items-center gap-2 text-sm text-[hsl(215,12%,62%)]">
              <Globe className="h-3.5 w-3.5 text-[var(--energy-green)] flex-shrink-0" />
              <span>{t('footer.domain')}</span>
            </div>
          </div>

          {/* Column 2: Navigation */}
          <div className="space-y-4">
            <p className="font-heading font-medium text-sm text-white uppercase tracking-widest">
              {t('footer.nav_title')}
            </p>
            <nav className="flex flex-col gap-2">
              {navLinks.map((link) => (
                <Link
                  key={link.path}
                  to={link.path}
                  className="text-sm text-[hsl(215,12%,62%)] hover:text-white transition-colors"
                >
                  {link.label}
                </Link>
              ))}
            </nav>
          </div>

          {/* Column 3: Legal + Language */}
          <div className="space-y-4">
            <p className="font-heading font-medium text-sm text-white uppercase tracking-widest">
              {t('footer.legal_title')}
            </p>
            <div className="flex flex-col gap-2">
              <Link
                to="/trust"
                className="text-sm text-[hsl(215,12%,62%)] hover:text-white transition-colors"
              >
                {t('footer.legal')}
              </Link>
              <Link
                to="/trust"
                className="text-sm text-[hsl(215,12%,62%)] hover:text-white transition-colors"
              >
                {t('footer.privacy')}
              </Link>
            </div>

            <div className="pt-2">
              <p className="text-xs text-[hsl(215,12%,50%)] mb-2 uppercase tracking-wider">
                Language
              </p>
              <div className="flex gap-1.5">
                {languages.map((lang) => (
                  <button
                    key={lang.code}
                    onClick={() => changeLanguage(lang.code)}
                    className={`px-2.5 py-1 text-xs font-mono-nums rounded-md transition-colors ${
                      currentLang === lang.code
                        ? 'bg-[var(--energy-green)] text-white'
                        : 'bg-[hsl(222,18%,18%)] text-[hsl(215,12%,62%)] hover:text-white'
                    }`}
                    data-testid={`footer-lang-${lang.code}`}
                  >
                    {lang.label}
                  </button>
                ))}
              </div>
            </div>
          </div>
        </div>

        <Separator className="my-8 bg-[hsl(222,18%,18%)]" />

        <div className="flex flex-col md:flex-row items-start md:items-center justify-between gap-2">
          <p className="text-xs text-[hsl(215,12%,45%)]">
            &copy; {new Date().getFullYear()} {t('footer.company')} · {t('footer.rights')}
          </p>
          <p className="text-xs text-[hsl(215,12%,40%)]">
            {t('footer.domain')}
          </p>
        </div>
      </div>
    </footer>
  );
}
